/**
 * Backoffice CRM: Test users (#1922), on the Users page. Accounts whose use is not counted as real use: analytics and
 * the CRM leave them out, while emails, features and limits stay the same for them.
 *
 * Three views, each read when it is opened:
 * - Addresses: the typed list in `globals/test_users.manual` (addresses, or @domain for a whole domain), and the
 *   TestFlight testers' addresses the daily sync copies next to it, with a Sync now.
 * - Test accounts: every account marked `test_user`, with why.
 * - Created with a debug build: accounts a debug build created (`created_in: "debug"`), to select and delete after the
 *   server's checks (adminDeleteDebugAccounts), shown first as a preview.
 */

import { BADGE, BUTTON, FIELD, TABLE, checkbox } from "./ui/classes.js";
import { solidIcon } from "./ui/icons.js";
import { showDialog, hideDialog } from "./ui/dialog.js";

/** What each reason means, as the user card and the lists say it. */
export const TEST_USER_REASON_LABELS = {
  debug_build: "Debug build",
  testflight: "TestFlight build",
  email_list: "Listed address",
  testflight_sync: "TestFlight tester",
  manual: "Marked here"
};

export function reasonBadgesHTML(reasons = []) {
  return reasons
    .map((reason) => `<span class="${BADGE.yellow}">${escapeHTML(TEST_USER_REASON_LABELS[reason] || reason)}</span>`)
    .join(" ");
}

/** A typed entry, normalised as the functions read it (test_users.js): an address, or @domain. Null if neither. */
export function normalizeListEntry(raw) {
  const value = String(raw ?? "").trim().toLowerCase();
  if (/^@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(value)) return value;
  if (/^[^\s@]+@[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}$/.test(value)) return value;
  return null;
}

/** The textarea's lines as the list to store: normalised, once each, sorted; and the lines that are neither. */
export function parseListText(text) {
  const entries = new Set();
  const invalid = [];
  String(text || "").split(/[\n,;]+/).map((line) => line.trim()).filter(Boolean).forEach((line) => {
    const entry = normalizeListEntry(line);
    if (entry) entries.add(entry);
    else invalid.push(line);
  });
  return { entries: [...entries].sort(), invalid };
}

let ctx = null;
let view = null;
let debugAccounts = [];
let pendingDelete = [];

const VIEW_IDS = ["list", "accounts", "debug"];
// A table cell whose text may wrap (TABLE.td keeps to one line).
const TD_WRAP = "px-3 py-4 text-sm text-gray-500 dark:text-gray-400";

/** Binds the view buttons and the delete dialog; reads nothing until an admin opens a view. */
export function initTestUsersPanel(context) {
  ctx = context;
  document.querySelectorAll("[data-test-users-view]").forEach((button) => {
    button.addEventListener("click", () => openView(button.dataset.testUsersView));
  });
  document.getElementById("testUsersDeleteConfirm")?.addEventListener("click", () => confirmDelete());
}

export async function openView(next) {
  if (!VIEW_IDS.includes(next)) return;
  view = next;
  document.querySelectorAll("[data-test-users-view]").forEach((button) => {
    button.setAttribute("aria-pressed", String(button.dataset.testUsersView === next));
  });
  if (next === "list") return loadList();
  return loadAccounts(next === "debug" ? "debug_created" : "test_users");
}

function body() {
  return document.getElementById("testUsersBody");
}

function loadingHTML(text) {
  return `<p class="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"><span class="block size-4 animate-spin rounded-full border-2 border-gray-300 border-t-paprika-600 dark:border-white/20 dark:border-t-paprika-400"></span>${escapeHTML(text)}</p>`;
}

function errorHTML(text) {
  return `<div class="rounded-md bg-red-50 p-4 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">${escapeHTML(text)}</div>`;
}

const dateText = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !isNaN(d.getTime()) ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";
};

const toIso = (value) => (typeof value?.toDate === "function" ? value.toDate().toISOString() : value || null);

// MARK: - Addresses

async function loadList() {
  const el = body();
  if (!el) return;
  el.innerHTML = loadingHTML("Loading the list…");
  try {
    const snap = await ctx.getDoc(ctx.doc(ctx.db, "globals", "test_users"));
    if (view !== "list") return;
    el.innerHTML = listViewHTML(snap.exists() ? snap.data() || {} : {});
    document.getElementById("testUsersSaveList")?.addEventListener("click", () => saveList());
    document.getElementById("testUsersSyncNow")?.addEventListener("click", () => syncNow());
  } catch (err) {
    el.innerHTML = errorHTML(`Could not read globals/test_users: ${err.message}`);
  }
}

export function listViewHTML(data) {
  const typed = Array.isArray(data.manual) ? data.manual : [];
  const testers = Array.isArray(data.testflight) ? data.testflight : [];
  const synced = toIso(data.testflight_synced_at);
  const error = data.testflight_error
    ? `<div class="mt-3 flex gap-3 rounded-md bg-red-50 p-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">${solidIcon("exclamation-triangle", "size-5 flex-none text-red-500")}<p><strong class="font-semibold">The last sync failed${data.testflight_error_at ? ` (${escapeHTML(dateText(toIso(data.testflight_error_at)))})` : ""}:</strong> ${escapeHTML(data.testflight_error)}</p></div>`
    : "";
  return `
    <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
      <div>
        <label for="testUsersManualInput" class="${FIELD.label}">Addresses and domains</label>
        <textarea id="testUsersManualInput" rows="8" placeholder="anton@example.com&#10;@weeknightbiteapp.com" class="mt-2 ${FIELD.input} font-mono">${escapeHTML(typed.join("\n"))}</textarea>
        <p class="${FIELD.help}">One per line: an address, or <code class="font-mono text-xs">@domain</code> for every address there. An account whose sign-in email matches is marked on saving (Listed address); one that no longer matches loses that reason only. Add Play Console's internal testers and license testers here too: Google offers no list of them.</p>
        <p id="testUsersListError" class="${FIELD.error}" style="display: none;"></p>
        <div class="mt-4"><button type="button" id="testUsersSaveList" class="${BUTTON.primary}">Save</button></div>
      </div>
      <div>
        <h4 class="text-sm/6 font-medium text-gray-900 dark:text-white">TestFlight testers</h4>
        <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">${testers.length} address${testers.length === 1 ? "" : "es"} from App Store Connect, ${synced ? `synced ${escapeHTML(dateText(synced))}` : "not synced yet"}. Only testers who sign in to the app with their Apple ID's email match; TestFlight builds mark their accounts themselves.</p>
        ${error}
        <div class="mt-4"><button type="button" id="testUsersSyncNow" class="${BUTTON.secondary}">${solidIcon("arrow-path", "size-4 text-gray-400")}Sync now</button></div>
        ${testers.length ? `<details class="mt-4 text-sm"><summary class="cursor-pointer font-medium text-gray-900 dark:text-white">Show the addresses</summary><ul class="mt-2 space-y-0.5 font-mono text-xs text-gray-600 dark:text-gray-300">${testers.map((e) => `<li>${escapeHTML(e)}</li>`).join("")}</ul></details>` : ""}
      </div>
    </div>`;
}

async function saveList() {
  const input = document.getElementById("testUsersManualInput");
  const errorEl = document.getElementById("testUsersListError");
  const button = document.getElementById("testUsersSaveList");
  const { entries, invalid } = parseListText(input?.value);
  if (errorEl) {
    errorEl.textContent = invalid.length ? `Not an address or @domain: ${invalid.join(", ")}` : "";
    errorEl.style.display = invalid.length ? "block" : "none";
  }
  if (invalid.length) return;
  if (entries.length > 500) {
    ctx.showToast("At most 500 addresses and domains.", "error");
    return;
  }
  if (button) button.disabled = true;
  try {
    await ctx.setDoc(ctx.doc(ctx.db, "globals", "test_users"), {
      manual: entries,
      updated_at: ctx.serverTimestamp(),
      updated_by: ctx.auth?.currentUser?.email || null
    }, { merge: true });
    if (input) input.value = entries.join("\n");
    ctx.showToast(`Saved ${entries.length} address${entries.length === 1 ? "" : "es"} and domains. Matching accounts are marked in a minute.`, "success");
  } catch (err) {
    ctx.showToast(`Could not save the list: ${err.message}`, "error");
  } finally {
    if (button) button.disabled = false;
  }
}

async function syncNow() {
  const button = document.getElementById("testUsersSyncNow");
  if (button) button.disabled = true;
  try {
    const { data } = await ctx.httpsCallable(ctx.functions, "syncTestUsersNow")({});
    if (data?.testflight?.ok) {
      ctx.showToast(`${data.testflight.count} TestFlight tester address${data.testflight.count === 1 ? "" : "es"}; ${data.applied?.changed || 0} account(s) changed.`, "success");
    } else {
      ctx.showToast(`The TestFlight sync failed: ${data?.testflight?.error || "no answer"}`, "error");
    }
  } catch (err) {
    ctx.showToast(`The sync failed: ${err.message}`, "error");
  } finally {
    if (view === "list") await loadList();
  }
}

// MARK: - Accounts

async function loadAccounts(kind) {
  const el = body();
  if (!el) return;
  el.innerHTML = loadingHTML("Loading the accounts…");
  try {
    const { data } = await ctx.httpsCallable(ctx.functions, "adminListTestAccounts")({ kind });
    if ((kind === "debug_created") !== (view === "debug")) return;
    if (kind === "debug_created") {
      debugAccounts = data.accounts || [];
      el.innerHTML = debugViewHTML(debugAccounts, data.truncated);
      bindDebugView();
    } else {
      el.innerHTML = accountsViewHTML(data.accounts || [], data.truncated);
    }
  } catch (err) {
    el.innerHTML = errorHTML(`Could not list the accounts: ${err.message}`);
  }
}

function accountCellHTML(a) {
  return `<a href="#users?uid=${encodeURIComponent(a.uid)}" class="${BUTTON.link}">${escapeHTML(a.email || "No email")}</a><div class="mt-0.5 font-mono text-xs break-all text-gray-500 dark:text-gray-400">${escapeHTML(a.uid)}</div>`;
}

function householdCellHTML(a) {
  return a.family_id
    ? `<a href="#families?id=${encodeURIComponent(a.family_id)}" class="${BUTTON.link} font-mono text-xs">${escapeHTML(a.family_id)}</a>`
    : `<span class="text-gray-400">—</span>`;
}

function tableHTML(headers, rows) {
  return `
    <div class="relative overflow-x-auto">
      <table class="${TABLE.table}">
        <thead><tr>${headers.map((h) => `<th scope="col" class="${TABLE.th}">${h}</th>`).join("")}</tr></thead>
        <tbody class="${TABLE.tbody}">${rows}</tbody>
      </table>
    </div>`;
}

function emptyHTML(text) {
  return `<div class="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-white/15 dark:text-gray-400">${escapeHTML(text)}</div>`;
}

const truncatedHTML = (truncated) => (truncated ? `<p class="mt-3 text-sm text-gray-500 dark:text-gray-400">The first 500 only.</p>` : "");

export function accountsViewHTML(accounts, truncated = false) {
  if (!accounts.length) return emptyHTML("No account is marked as a test user.");
  const rows = accounts.map((a) => `
    <tr>
      <td class="${TD_WRAP}">${accountCellHTML(a)}</td>
      <td class="${TD_WRAP}">${reasonBadgesHTML(a.test_user_reasons)}</td>
      <td class="${TABLE.td}">${escapeHTML(dateText(a.created_at))}</td>
      <td class="${TABLE.td}">${escapeHTML(dateText(a.last_sign_in_at))}</td>
      <td class="${TABLE.td}">${householdCellHTML(a)}</td>
    </tr>`).join("");
  return `<p class="mb-3 text-sm text-gray-500 dark:text-gray-400">${accounts.length} account${accounts.length === 1 ? "" : "s"}, newest first.</p>${tableHTML(["Account", "Why", "Created", "Last sign-in", "Household"], rows)}${truncatedHTML(truncated)}`;
}

export function debugViewHTML(accounts, truncated = false) {
  if (!accounts.length) return emptyHTML("No account was created with a debug build. Accounts made before debug builds recorded it don't show here.");
  const rows = accounts.map((a) => `
    <tr>
      <td class="relative px-7 sm:w-12 sm:px-6"><div class="absolute top-1/2 left-4 -mt-2">${checkbox(`data-debug-account="${escapeHTML(a.uid)}" aria-label="Select ${escapeHTML(a.email || a.uid)}"`)}</div></td>
      <td class="${TD_WRAP}">${accountCellHTML(a)}</td>
      <td class="${TABLE.td}">${escapeHTML(dateText(a.created_at))}</td>
      <td class="${TABLE.td}">${escapeHTML(dateText(a.last_sign_in_at))}</td>
      <td class="${TABLE.td}">${householdCellHTML(a)}</td>
    </tr>`).join("");
  return `
    <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
      <p class="text-sm text-gray-500 dark:text-gray-400">${accounts.length} account${accounts.length === 1 ? "" : "s"}, newest first. Select some to see what deleting them does; nothing is deleted before you confirm.</p>
      <button type="button" id="testUsersDeleteSelected" disabled class="${BUTTON.secondaryDanger}">${solidIcon("trash", "size-4")}Delete selected…</button>
    </div>
    ${tableHTML([`<span class="sr-only">Select</span>`, "Account", "Created", "Last sign-in", "Household"], rows)}${truncatedHTML(truncated)}`;
}

function selectedDebugUids() {
  return [...document.querySelectorAll("[data-debug-account]")].filter((box) => box.checked).map((box) => box.dataset.debugAccount);
}

function bindDebugView() {
  const button = document.getElementById("testUsersDeleteSelected");
  document.querySelectorAll("[data-debug-account]").forEach((box) => {
    box.addEventListener("change", () => {
      const count = selectedDebugUids().length;
      if (button) button.disabled = count === 0 || count > 50;
    });
  });
  button?.addEventListener("click", () => previewDelete(selectedDebugUids()));
}

/** The dialog's rows: what happens to each selected account. */
export function deletePlanHTML(accounts, byUid = new Map()) {
  return accounts.map((a) => {
    const who = escapeHTML(byUid.get(a.uid)?.email || a.uid);
    const outcome = a.action === "delete"
      ? `<span class="${BADGE.red}">Delete</span> ${a.household?.outcome === "deleted" ? `with its household <span class="font-mono text-xs">${escapeHTML(a.household.id)}</span>` : a.household ? `and leave household <span class="font-mono text-xs">${escapeHTML(a.household.id)}</span>` : ""}`
      : `<span class="${BADGE.gray}">Keep</span> ${escapeHTML(a.reason || "")}`;
    return `<li class="py-2 text-sm text-gray-700 dark:text-gray-300"><div class="font-medium text-gray-900 dark:text-white">${who}</div><div class="mt-1">${outcome}</div></li>`;
  }).join("");
}

async function previewDelete(uids) {
  if (!uids.length) return;
  const list = document.getElementById("testUsersDeletePlan");
  const confirm = document.getElementById("testUsersDeleteConfirm");
  if (list) list.innerHTML = `<li class="py-2">${loadingHTML("Checking each account…")}</li>`;
  if (confirm) confirm.disabled = true;
  showDialog("testUsersDeleteDialog");
  try {
    const { data } = await ctx.httpsCallable(ctx.functions, "adminDeleteDebugAccounts")({ uids, dryRun: true });
    const byUid = new Map(debugAccounts.map((a) => [a.uid, a]));
    pendingDelete = (data.accounts || []).filter((a) => a.action === "delete").map((a) => a.uid);
    if (list) list.innerHTML = deletePlanHTML(data.accounts || [], byUid);
    if (confirm) {
      confirm.disabled = pendingDelete.length === 0;
      confirm.textContent = pendingDelete.length ? `Delete ${pendingDelete.length} account${pendingDelete.length === 1 ? "" : "s"}` : "Nothing to delete";
    }
  } catch (err) {
    if (list) list.innerHTML = `<li class="py-2">${errorHTML(`The check failed: ${err.message}`)}</li>`;
  }
}

async function confirmDelete() {
  const uids = pendingDelete;
  if (!uids.length) return;
  const confirm = document.getElementById("testUsersDeleteConfirm");
  if (confirm) confirm.disabled = true;
  try {
    const { data } = await ctx.httpsCallable(ctx.functions, "adminDeleteDebugAccounts")({ uids, dryRun: false });
    const accounts = data.accounts || [];
    const deleted = accounts.filter((a) => a.deleted).length;
    const failed = accounts.filter((a) => a.action === "delete" && !a.deleted);
    const skipped = accounts.filter((a) => a.action === "skip").length;
    ctx.showToast(`Deleted ${deleted} account${deleted === 1 ? "" : "s"}${skipped ? `, kept ${skipped} that changed since the check` : ""}${failed.length ? `; ${failed.length} failed: ${failed.map((a) => a.error).join("; ")}` : ""}.`, failed.length ? "error" : "success");
    pendingDelete = [];
    hideDialog("testUsersDeleteDialog");
  } catch (err) {
    ctx.showToast(`Deleting failed: ${err.message}`, "error");
    if (confirm) confirm.disabled = false;
    return;
  }
  if (view === "debug") await loadAccounts("debug_created");
}

function escapeHTML(str) {
  if (typeof str !== "string") return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

